import { useEffect, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';

interface SpreadsheetPageDropTargetProps {
  enabled?: boolean;
  onFile: (file: File) => void;
  onUnsupportedFile?: (file: File) => void;
  label?: string;
}

const SPREADSHEET_EXTENSION = /\.(csv|xlsx|xls)$/i;

function isFileDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

/**
 * Turns the current page into a spreadsheet drop target without adding a
 * competing parser. The selected file is handed to the canonical Excel import
 * wizard by the parent.
 */
export function SpreadsheetPageDropTarget({
  enabled = true,
  onFile,
  onUnsupportedFile,
  label = 'Drop to map and import',
}: SpreadsheetPageDropTargetProps) {
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsDraggingFile(false);
      return;
    }

    let dragDepth = 0;

    const handleDragEnter = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      dragDepth += 1;
      setIsDraggingFile(true);
    };

    const handleDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = (event: DragEvent) => {
      if (dragDepth === 0) return;
      event.preventDefault();
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) setIsDraggingFile(false);
    };

    const handleDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      dragDepth = 0;
      setIsDraggingFile(false);

      const file = event.dataTransfer?.files?.[0];
      if (!file) return;
      if (!SPREADSHEET_EXTENSION.test(file.name)) {
        onUnsupportedFile?.(file);
        return;
      }
      onFile(file);
    };

    const handleDragEnd = () => {
      dragDepth = 0;
      setIsDraggingFile(false);
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    window.addEventListener('dragend', handleDragEnd);
    window.addEventListener('blur', handleDragEnd);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      window.removeEventListener('dragend', handleDragEnd);
      window.removeEventListener('blur', handleDragEnd);
    };
  }, [enabled, onFile, onUnsupportedFile]);

  if (!isDraggingFile) return null;

  return (
    <div className="pointer-events-none fixed inset-4 z-40 flex items-center justify-center border border-dashed border-primary-500 bg-primary-50/95">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="bg-[var(--bg-raised)] p-3 text-primary-600">
          <FileSpreadsheet className="h-8 w-8" />
        </div>
        <div>
          <p className="font-heading text-lg font-semibold text-[var(--text-pri)]">{label}</p>
          <p className="mt-1 text-sm text-[var(--text-sec)]">CSV, XLSX, or XLS</p>
        </div>
      </div>
    </div>
  );
}
